import { FC, useState } from 'react';
import { NumericFormat } from 'react-number-format';
import { Field, FieldProps, useFormikContext } from 'formik';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import capitalize from 'lodash/capitalize';
import omit from 'lodash/omit';

import { useStyles } from './AnotherOfferInvolvedBlock.styles';
import {
  MONEY_SEPARATOR,
  compensationCompetitivenessOptions,
  offerAcceptanceStatusOptions,
} from '../../constants/constants';
import { FlagFormFields } from '../../constants/FlagFormFields';
import { EmploymentType } from '@shared/enums/EmploymentType';
import { currencies } from '@shared/enums/currencies';
import { FlagFields } from '@modules/HappinessModule/interfaces/FlagFields';
import { AmountType } from '@modules/HappinessModule/enums/AmountType';
import { FlagPermissionsData } from '@modules/HappinessModule/pages/FlagPage/interfaces/FlagPermissionsData';
interface Props {
  userFlagPermissions: FlagPermissionsData;
  isNewFlag?: boolean;
}

export const AnotherOfferInvolvedBlock: FC<Props> = ({ userFlagPermissions, isNewFlag }) => {
  const styles = useStyles();
  const { values, setFieldValue } = useFormikContext<FlagFields>();

  const [benefitsNotProvided, setBenefitsNotProvided] = useState(values.benefitsNotProvided === true);

  const handleNotProvided = (
    flag: boolean,
    checkboxFieldName: keyof Pick<
      FlagFields,
      | 'companyNameNotProvided'
      | 'compensationNotProvided'
      | 'roleNotProvided'
      | 'hiringTypeNotProvided'
      | 'currencyTypeNotProvided'
      | 'amountTypeNotProvided'
    >,
    fieldName: keyof FlagFields,
    resetValue: string | number = ''
  ) => {
    setFieldValue(checkboxFieldName, flag);
    if (flag) {
      setFieldValue(fieldName, null);
    } else {
      setFieldValue(fieldName, resetValue);
    }
  };

  const handleBenefitsNotProvided = (flag: boolean) => {
    setBenefitsNotProvided(flag);
    setFieldValue('benefitsNotProvided', flag);
    if (flag) {
      anotherOfferCheckBoxFields.forEach((checkboxField) => {
        setFieldValue(checkboxField.name, false);
      });
      setFieldValue(FlagFormFields.benefits.name, '');
    }
  };

  const isAnotherOfferSwticherDisabled = isNewFlag
    ? !userFlagPermissions.flag.isCreatable
    : !userFlagPermissions.flag.isUpdatable;

  const isCompensationDetailsDisabled = isNewFlag
    ? !userFlagPermissions.compensationDetails.isCreatable
    : !userFlagPermissions.compensationDetails.isUpdatable;

  const isCompensationDetailsReadable =
    userFlagPermissions.compensationDetails.isReadable || userFlagPermissions.compensationDetails.isCreatable;

  const anotherOfferCheckBoxFields = [
    FlagFormFields.pto_incl,
    FlagFormFields.paid_holidays,
    FlagFormFields.parental_leave,
    FlagFormFields.health_ins,
    FlagFormFields.wellness,
    FlagFormFields.prof_dev,
    FlagFormFields.thirteenth_salary,
    FlagFormFields.stock_opt,
    FlagFormFields.bonuses,
  ];

  return (
    <Paper variant="elevation" elevation={2}>
      <Grid
        container
        className={
          isCompensationDetailsReadable
            ? values.isAnotherOfferInvolved
              ? styles.anotherOfferInvolvedBlockHrViewExpaneded
              : styles.anotherOfferInvolvedBlockHrViewCollapsed
            : styles.mainContainer
        }
        columnSpacing={1}
        rowGap={values.isAnotherOfferInvolved ? '23px' : '0px'}
      >
        <Grid item xs={12}>
          <Field
            name={FlagFormFields.isAnotherOfferInvolved.name}
            type="checkbox"
            component={({ field }: FieldProps) => (
              <FormControlLabel
                control={<Switch disabled={isAnotherOfferSwticherDisabled} {...field} />}
                label={<Typography variant="h2">{FlagFormFields.isAnotherOfferInvolved.label}</Typography>}
              />
            )}
          />
        </Grid>
        <Grid item xs={4}>
          <Stack rowGap="35px">
            {isCompensationDetailsReadable && values.isAnotherOfferInvolved && (
              <Stack rowGap="16px">
                <Typography variant="subtitle1">Company and employment type</Typography>
                <Field name={FlagFormFields.companyName.name}>
                  {({ field }: FieldProps) => (
                    <Stack flexDirection="row" alignItems="center" columnGap="8px">
                      <TextField
                        label={FlagFormFields.companyName.label}
                        variant="outlined"
                        fullWidth
                        {...field}
                        value={field.value ?? ''}
                        disabled={isCompensationDetailsDisabled || values.companyNameNotProvided}
                      />
                    </Stack>
                  )}
                </Field>
                <Field name={FlagFormFields.hiringType.name} as={RadioGroup}>
                  <Stack columnGap="24px" flexDirection="row">
                    {Object.entries(EmploymentType).map(([, value], i) => (
                      <FormControlLabel
                        key={i}
                        control={<Radio />}
                        label={value}
                        value={value}
                        disabled={isCompensationDetailsDisabled || values.hiringTypeNotProvided}
                      />
                    ))}
                  </Stack>
                </Field>
                <Field name={FlagFormFields.role.name}>
                  {({ field }: FieldProps) => (
                    <Stack flexDirection="row" alignItems="center" columnGap="8px">
                      <TextField
                        label={FlagFormFields.role.label}
                        variant="outlined"
                        fullWidth
                        {...field}
                        value={field.value ?? ''}
                        disabled={isCompensationDetailsDisabled || values.roleNotProvided}
                      />
                    </Stack>
                  )}
                </Field>
              </Stack>
            )}
          </Stack>
        </Grid>
        {values.isAnotherOfferInvolved && isCompensationDetailsReadable && (
          <Grid item xs={4}>
            <Stack rowGap="16px">
              <Typography variant="subtitle1">Compensation</Typography>
              <Stack flexDirection="row" columnGap="8px" alignItems="center">
                <Field name={FlagFormFields.compensation.name}>
                  {({ field }: FieldProps) => {
                    const fieldForNumeric = omit(field, 'onChange');
                    return (
                      <NumericFormat
                        disabled={isCompensationDetailsDisabled || values.compensationNotProvided}
                        thousandSeparator={MONEY_SEPARATOR}
                        min={0}
                        max={100000}
                        allowNegative={false}
                        customInput={TextField}
                        label={FlagFormFields.compensation.label}
                        variant="outlined"
                        fullWidth
                        {...fieldForNumeric}
                        onValueChange={(vals) => {
                          setFieldValue(FlagFormFields.compensation.name, vals.floatValue, false);
                        }}
                        value={field.value ?? ''}
                      />
                    );
                  }}
                </Field>
                <Field
                  name={FlagFormFields.currencyType.name}
                  component={({ field }: FieldProps) => (
                    <FormControl fullWidth disabled={isCompensationDetailsDisabled || values.currencyTypeNotProvided}>
                      <InputLabel>{FlagFormFields.currencyType.label}</InputLabel>
                      <Select label={FlagFormFields.currencyType.label} {...field}>
                        {Object.entries(currencies)
                          .sort((a, b) => a[1].localeCompare(b[1]))
                          .map(([, value]) => (
                            <MenuItem value={value} key={value}>
                              {value}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Stack>
              <Field name={FlagFormFields.amountType.name} as={RadioGroup}>
                <Stack columnGap={1} flexDirection={'row'}>
                  {Object.entries(AmountType).map(([, value], i) => (
                    <FormControlLabel
                      key={i}
                      disabled={isCompensationDetailsDisabled || values.amountTypeNotProvided}
                      control={<Radio />}
                      label={capitalize(value)}
                      value={value.toLowerCase()}
                    />
                  ))}
                </Stack>
              </Field>
              <Field
                name={FlagFormFields.compensationCompetitiveness.name}
                component={({ field }: FieldProps) => (
                  <FormControl fullWidth disabled={isCompensationDetailsDisabled}>
                    <InputLabel>{FlagFormFields.compensationCompetitiveness.label}</InputLabel>
                    <Select label={FlagFormFields.compensationCompetitiveness.label} {...field}>
                      {compensationCompetitivenessOptions.map(({ value, description }) => (
                        <MenuItem value={value} key={value}>
                          {description}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Stack>
          </Grid>
        )}

        {values.isAnotherOfferInvolved && isCompensationDetailsReadable && (
          <Grid item xs={4} paddingTop={5}>
            <Stack rowGap="25px">
              <Field
                name={FlagFormFields.offerAcceptanceStatus.name}
                component={({ field }: FieldProps) => (
                  <FormControl fullWidth disabled={isCompensationDetailsDisabled}>
                    <InputLabel>{FlagFormFields.offerAcceptanceStatus.label}</InputLabel>
                    <Select label={FlagFormFields.offerAcceptanceStatus.label} {...field}>
                      {offerAcceptanceStatusOptions.map(({ value, description }) => (
                        <MenuItem value={value} key={value}>
                          {description}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
              {values.offerAcceptanceStatus === 'rejected' && (
                <Field name={FlagFormFields.offerRejectionReason.name}>
                  {({ field }: FieldProps) => (
                    <TextField
                      label={FlagFormFields.offerRejectionReason.label}
                      variant="outlined"
                      fullWidth
                      multiline
                      minRows={3.5}
                      {...field}
                      disabled={isCompensationDetailsDisabled}
                    />
                  )}
                </Field>
              )}
            </Stack>
          </Grid>
        )}

        {isCompensationDetailsReadable && values.isAnotherOfferInvolved && (
          <Grid item xs={12}>
            <Typography variant="subtitle1" marginBottom={2}>
              Benefits and bonuses
            </Typography>

            <Box sx={{ display: 'flex' }}>
              <Grid item xs={4} display="grid">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={benefitsNotProvided}
                      onChange={(e) => handleBenefitsNotProvided(e.target.checked)}
                      disabled={isCompensationDetailsDisabled}
                      sx={{ paddingBlock: 0 }}
                    />
                  }
                  label="Not provided"
                  sx={{ marginBottom: 1 }}
                />
                {anotherOfferCheckBoxFields.map((anotherOfferCheckbox) => (
                  <Field
                    name={anotherOfferCheckbox?.name}
                    type="checkbox"
                    component={({ field }: FieldProps) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            disabled={isCompensationDetailsDisabled || benefitsNotProvided}
                            {...field}
                            value={field.value ?? ''}
                            sx={{ paddingBlock: 0 }}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography>{anotherOfferCheckbox?.label}</Typography>
                            {anotherOfferCheckbox?.helperText && (
                              <Box component="span" sx={{ typography: 'subtitle2', color: 'text.secondary' }}>
                                {anotherOfferCheckbox.helperText}
                              </Box>
                            )}
                          </Box>
                        }
                        sx={{ marginBottom: 1, alignItems: anotherOfferCheckbox?.helperText ? 'flex-start' : 'center' }}
                      />
                    )}
                  />
                ))}
              </Grid>
              <Grid item xs={4} marginLeft={1}>
                <Field name={FlagFormFields.benefits.name}>
                  {({ field }: FieldProps) => (
                    <TextField
                      disabled={isCompensationDetailsDisabled || benefitsNotProvided}
                      multiline
                      minRows={16}
                      label={FlagFormFields.benefits.label}
                      variant="outlined"
                      fullWidth
                      {...field}
                    />
                  )}
                </Field>
              </Grid>
              <Grid item xs={4} marginLeft={1}>
                <Stack>
                  <Typography variant="subtitle1">Not provided information</Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.companyNameNotProvided}
                        onChange={(e) =>
                          handleNotProvided(
                            e.target.checked,
                            FlagFormFields.companyNameNotProvided.name,
                            FlagFormFields.companyName.name,
                            ''
                          )
                        }
                        disabled={isCompensationDetailsDisabled}
                        sx={{ paddingBlock: 0 }}
                      />
                    }
                    label={FlagFormFields.companyNameNotProvided.label}
                    sx={{ whiteSpace: 'nowrap', mr: 0 }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.compensationNotProvided}
                        onChange={(e) =>
                          handleNotProvided(
                            e.target.checked,
                            FlagFormFields.compensationNotProvided.name,
                            FlagFormFields.compensation.name,
                            0
                          )
                        }
                        disabled={isCompensationDetailsDisabled}
                        sx={{ paddingBlock: 0 }}
                      />
                    }
                    label={FlagFormFields.compensationNotProvided.label}
                    sx={{ whiteSpace: 'nowrap', mr: 0 }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.roleNotProvided}
                        onChange={(e) =>
                          handleNotProvided(
                            e.target.checked,
                            FlagFormFields.roleNotProvided.name,
                            FlagFormFields.role.name,
                            ''
                          )
                        }
                        disabled={isCompensationDetailsDisabled}
                        sx={{ paddingBlock: 0 }}
                      />
                    }
                    label={FlagFormFields.roleNotProvided.label}
                    sx={{ whiteSpace: 'nowrap', mr: 0 }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.hiringTypeNotProvided}
                        onChange={(e) =>
                          handleNotProvided(
                            e.target.checked,
                            FlagFormFields.hiringTypeNotProvided.name,
                            FlagFormFields.hiringType.name
                          )
                        }
                        disabled={isCompensationDetailsDisabled}
                        sx={{ paddingBlock: 0 }}
                      />
                    }
                    label={FlagFormFields.hiringTypeNotProvided.label}
                    sx={{ whiteSpace: 'nowrap', mr: 0 }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.currencyTypeNotProvided}
                        onChange={(e) =>
                          handleNotProvided(
                            e.target.checked,
                            FlagFormFields.currencyTypeNotProvided.name,
                            FlagFormFields.currencyType.name
                          )
                        }
                        disabled={isCompensationDetailsDisabled}
                        sx={{ paddingBlock: 0 }}
                      />
                    }
                    label={FlagFormFields.currencyTypeNotProvided.label}
                    sx={{ whiteSpace: 'nowrap', mr: 0 }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.amountTypeNotProvided}
                        onChange={(e) =>
                          handleNotProvided(
                            e.target.checked,
                            FlagFormFields.amountTypeNotProvided.name,
                            FlagFormFields.amountType.name
                          )
                        }
                        disabled={isCompensationDetailsDisabled}
                        sx={{ paddingBlock: 0 }}
                      />
                    }
                    label={FlagFormFields.amountTypeNotProvided.label}
                    sx={{ whiteSpace: 'nowrap', mr: 0 }}
                  />
                </Stack>
              </Grid>
            </Box>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};
